<template>
    <Component :is="sliceVariationComponent" v-bind="props" />
</template>

<script setup lang="ts">
    import {computed} from 'vue';
    import {getSliceComponentProps} from '@prismicio/vue';
    import type {HeroSlice} from '@prismicio-types';
    import HeroDefault from './variations/HeroDefault.vue';
    import HeroProduct from './variations/HeroProduct.vue';
    import HeroStrategy from './variations/HeroStrategy.vue';
    import HeroWithBackground from './variations/HeroWithBackgroud.vue';
    import HeroWithForm from './variations/HeroWithForm.vue';
    import HeroWithImages from './variations/HeroWithImages.vue';
    import HeroWithSearch from './variations/HeroWithSearch.vue';
    import {getSliceVariationComponent} from '../../utils/sliceVariations';

    const props = defineProps(getSliceComponentProps<HeroSlice>());

    const variationComponents = {
        default: HeroDefault,
        heroWithImages: HeroWithImages,
        heroWithForm: HeroWithForm,
        heroWithSearch: HeroWithSearch,
        heroStrategy: HeroStrategy,
        heroProduct: HeroProduct,
        heroWithBackground: HeroWithBackground,
    };

    const sliceVariationComponent = computed(() => getSliceVariationComponent(props.slice, variationComponents));
</script>
<style lang="scss">
    @use '@malt/joy-entrypoint/src/sass/base/2_mixins/media-queries/media-queries' as mq;

    $flex-gap: 48px;

    .slice-hero {
        &--hide-in-mobile {
            display: none;

            @include mq.screen_sm(min) {
                display: block;
            }
        }

        &__logo {
            max-width: 150px;
            height: auto;
            margin-bottom: var(--joy-core-spacing-4);

            @include mq.screen_md(min) {
                max-width: 250px;
                margin-bottom: var(--joy-core-spacing-8);
            }
        }

        &__title h1 {
            font-family: var(--joy-font-family-title);
            font-weight: var(--joy-font-weight-normal);
            font-size: var(--joy-font-size-secondary-400);
            line-height: var(--joy-line-height-small);
            color: var(--joy-color-neutral-60);

            strong {
                font-family: var(--joy-font-family-title);
                font-weight: var(--joy-font-weight-normal);
                font-style: normal;
                background-color: var(--joy-color-brand-primary-30);
                white-space: nowrap;
                border-radius: 9999px;
                padding: 2px 10px;
                margin: 0 -5px;
            }

            @include mq.screen_sm(min) {
                margin-bottom: 30px;
                font-size: var(--joy-font-size-secondary-800);

                strong {
                    padding: 2px 15px;
                }
            }
        }

        &__description {
            margin-top: 24px;
            font-weight: var(--joy-font-weight-normal);
            font-size: var(--joy-font-size-primary-600);
            line-height: var(--joy-line-height-large);
            color: var(--joy-color-neutral-50);

            a:hover {
                text-decoration: underline;
            }

            a {
                color: var(--joy-color-secondary-30);

                @include mq.screen_sm() {
                    color: inherit;
                    text-decoration: underline;
                }
            }
        }

        &__content-wrapper {
            display: flex;
            gap: $flex-gap;
            align-items: center;
        }

        &__content {
            width: 100%;

            @include mq.screen_md(min) {
                width: 50%;
            }
        }

        &__ctas {
            display: flex;
            flex-direction: column;
            gap: 20px;
            margin-top: 48px;
            width: 100%;

            @include mq.screen_xs(min) {
                flex-direction: row;
                width: inherit;
            }

            .joy-button {
                width: 100%;

                @include mq.screen_sm(min) {
                    width: auto;
                }
            }
        }

        &__image {
            display: none;

            @include mq.screen_md(min) {
                display: block;
                width: 50%;
                height: auto;

                img {
                    width: 100%;
                    height: auto;
                    border-radius: var(--joy-core-radius-4);
                    vertical-align: middle;
                }
            }

            @media (max-height: 800px) {
                img {
                    width: 500px;
                }
            }
        }
    }
</style>
